import { deviceApi } from "#/api/deviceApi";
import { BarChart } from "@mantine/charts";
import { Select } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

interface UsageSectionProps {
  deviceId: number;
  clientId: number;
}

function formatDate(date: Date): string {
  return date.toISOString().split("T")[0];
}

function getDateRange(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (days - 1));
  return { from: formatDate(from), to: formatDate(to) };
}

const rangeOptions = [
  { value: "7", label: "Last 7 days" },
  { value: "14", label: "Last 14 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];

export default function UsageSection({ deviceId, clientId }: UsageSectionProps) {
  const [rangeDays, setRangeDays] = useState("7");
  const { from, to } = useMemo(() => getDateRange(Number(rangeDays)), [rangeDays]);

  const { data: allStats = [], isPending } = useQuery({
    queryKey: ["device-daily-stats", clientId, from, to],
    queryFn: async () => {
      const response = await deviceApi.getDailyStats(clientId, from, to);
      return response.data;
    },
  });

  // Filter to this device only
  const stats = useMemo(
    () => allStats.filter((s) => s.deviceId === deviceId),
    [allStats, deviceId],
  );

  // Chart data — fill missing days with 0
  const chartData = useMemo(() => {
    const days = Number(rangeDays);
    const statsMap = new Map(stats.map((s) => [s.date, s.scanCount]));
    const result: { date: string; scanCount: number }[] = [];

    for (let i = days - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = formatDate(d);
      result.push({
        date: key,
        scanCount: statsMap.get(key) ?? 0,
      });
    }
    return result;
  }, [stats, rangeDays]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Select
          data={rangeOptions}
          value={rangeDays}
          onChange={(v) => setRangeDays(v ?? "7")}
          w={160}
          size="sm"
        />
      </div>

      {isPending ? (
        <div className="h-[300px] flex items-center justify-center text-gray-400">
          Loading...
        </div>
      ) : (
        <BarChart
          h={300}
          data={chartData}
          dataKey="date"
          series={[{ name: "scanCount", color: "blue.6" }]}
          tickLine="y"
          gridAxis="y"
        />
      )}
    </div>
  );
}
